import React, {
    Dispatch,
    SetStateAction,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {threadType} from '../types/thread'
import {AssistantApi} from '../services/AssistantApi.service'
import {messageType} from '../types/message'
import {TribunaisType} from '../types/tribunais'
import {AssistantType} from '../types/assistant'
import {CookiesService} from '../services/Cookie.service'
import {limitsType} from '../types/limits'
import {project} from '../types/project'
import {Question} from '../types/question'
import {Organization} from '../types/orgUsers'
import {OrgMetrics} from '../types/orgUsersMetrics'
import {DocumentApi} from '../services/DocumentApi.service'
import {webSocketMessage} from '../types/websocket'
import {AuthContext} from './auth'
import {userDocumentsInfo} from '../types/userDocumentsInfo'
import {IUserInfo} from '../types/user'
import {FlowApi} from '../services/FlowApi.service'
import {QuestionFlow} from 'types/flowData'

const limitsTemplate = {
    created_at: '',
    usages: {
        created_at: '',
        messages: {
            max: 1,
            current: 1,
        },
        projects: {
            max: 0,
            current: 0,
        },
    },
}

interface SubscriptionType {
    created_at: string
    guid: string
    issued_at: string
    exp_at: string
    is_active: boolean
    price: number
    stripe_subscription_id: string
    pending_activation: boolean
}

export interface Project {
    created_at: string
    name: string
    description: string
    updated_at: string
    start_date: string
    end_date: string
    is_active: boolean
    project_type_id: string
    status: string
}

interface IHistoryContext {
    setUnarchieveModal: Dispatch<SetStateAction<boolean>>
    selected: threadType | null
    setSelected: Dispatch<SetStateAction<threadType | null>>
    historyArray: threadType[]
    createNewChat: () => Promise<void>
    messagesArray: messageType[]
    includeMessageAtArray: (message: messageType) => void
    tribunal: TribunaisType
    setTribunal: Dispatch<SetStateAction<TribunaisType>>
    assistant: AssistantType | null
    setAssistant: Dispatch<SetStateAction<AssistantType | null>>
    limits: limitsType
    assistants: AssistantType[]
    getLimits: () => void
    archiveThread: (guid: string) => void
    unarchiveThread: (guid: string) => void
    deleteThread: (guid: string) => void
    updateCurrentThread: (message: messageType) => void
    subscription: SubscriptionType | null
    alertVisibility: string
    archivedThread?: threadType[]
    archivedProjects?: project[]
    unarchieveModal: boolean
    isChat: boolean
    setIsChat: Dispatch<SetStateAction<boolean>>
    projectArray: project[]
    questionArray: Question[]
    setQuestionArray: Dispatch<SetStateAction<Question[]>>
    getNextQuestion: (project_id: string) => void
    getProjects: () => void
    usersOrg: Organization[]
    setMessagesArray: Dispatch<SetStateAction<messageType[]>>
    postProject: (projectData?: any) => Promise<void>
    archiveProject: (project_id: string) => void
    unarchiveProject: (project_id: string) => void
    webSocketMessage: webSocketMessage
    openModelAlertLimit: boolean
    setOpenModelAlertLimit: Dispatch<SetStateAction<boolean>>
    orgUsersMetrics: OrgMetrics
    getOrgUsersMetrics: () => void
    loadingMessageBot: boolean
    setLoadingMessageBot: Dispatch<SetStateAction<boolean>>
    userDocumentsInfo: userDocumentsInfo | null
    uuid: string
    setUuid: Dispatch<SetStateAction<string>>
    updateProject: (projectData?: any) => Promise<void>
    setOrganizationMaxUsages: (
        metricas: {[guid: string]: {messages?: number; projects?: number}},
        defaultResources: boolean
    ) => Promise<void>
    projectCost: number
    setProjectCost: Dispatch<SetStateAction<number>>
    selectedFiles: File[]
    setSelectedFiles: Dispatch<SetStateAction<File[]>>
    user: IUserInfo | null
    viewResume: boolean
    setViewResume: Dispatch<SetStateAction<boolean>>
    redSocketMessage: webSocketMessage
    setRedSocketMessage: Dispatch<SetStateAction<webSocketMessage>>
    priceResearchSocketMessage: webSocketMessage
    setPriceResearchSocketMessage: Dispatch<SetStateAction<webSocketMessage>>
    caronaSocketMessage: webSocketMessage
    setCaronaSocketMessage: Dispatch<SetStateAction<webSocketMessage>>
    radarSocketMessage: webSocketMessage
    setRadarSocketMessage: Dispatch<SetStateAction<webSocketMessage>>
    flowQuestions: QuestionFlow | null
    setFlowData: (id: string) => Promise<void>
    flows: Array<{id: string; name: string}>
    setFlows: Dispatch<SetStateAction<Array<{id: string; name: string}>>>
    updateFlow: (flowData: any) => Promise<void>
    getFlow: () => void
}

export const HistoryContext = createContext<IHistoryContext>({
    selected: {
        owner_id: '0',
        guid: '0',
        name: '',
        created_at: '',
        last_updated: '',
        uploaded_documents: 0,
    },
    setUnarchieveModal: () => {},
    setIsChat: () => {},
    setSelected: () => {},
    historyArray: [],
    archivedThread: [],
    archivedProjects: [],
    createNewChat: () => {
        return Promise.resolve()
    },
    messagesArray: [],
    includeMessageAtArray: () => {},
    tribunal: 'Tribunais Superiores',
    setTribunal: () => {},
    assistant: null,
    setAssistant: () => {},
    limits: limitsTemplate,
    assistants: [],
    subscription: null,
    getLimits: () => {},
    archiveThread: () => {},
    unarchiveThread: () => {},
    deleteThread: () => {},
    updateCurrentThread: () => {},
    alertVisibility: 'transparent',
    unarchieveModal: false,
    isChat: true,
    projectArray: [],
    questionArray: [],
    setQuestionArray: () => {},
    getNextQuestion: () => {},
    getProjects: () => {},
    setMessagesArray: () => {},
    postProject: () => {
        return Promise.resolve()
    },
    archiveProject: () => {},
    unarchiveProject: () => {},
    usersOrg: [],
    webSocketMessage: {
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    },
    openModelAlertLimit: false,
    setOpenModelAlertLimit: () => {},
    orgUsersMetrics: {
        org_name: '',
        org_guid: '',
        max_answers: 0,
        used_answers: 0,
        max_docs: 0,
        used_docs: 0,
        users_metrics: [],
    },
    getOrgUsersMetrics: () => {},
    loadingMessageBot: false,
    setLoadingMessageBot: () => {},
    userDocumentsInfo: null,
    uuid: '',
    setUuid: () => {},
    updateProject: () => {
        return Promise.resolve()
    },
    setOrganizationMaxUsages: () => {
        return Promise.resolve()
    },
    projectCost: 0,
    setProjectCost: () => {},
    selectedFiles: [],
    setSelectedFiles: () => {},
    user: null,
    viewResume: false,
    setViewResume: () => {},
    redSocketMessage: {
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    },
    setRedSocketMessage: () => {},
    priceResearchSocketMessage: {
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    },
    setPriceResearchSocketMessage: () => {},
    caronaSocketMessage: {
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    },
    setCaronaSocketMessage: () => {},
    radarSocketMessage: {
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    },
    setRadarSocketMessage: () => {},
    flowQuestions: null,
    setFlowData: () => {
        return Promise.resolve()
    },
    flows: [],
    setFlows: () => {},
    updateFlow: () => {
        return Promise.resolve()
    },
    getFlow: () => {},
})

const HistoryProvider = ({
    children,
}: {
    children: React.ReactNode
}): JSX.Element => {
    const {userInfo} = useContext(AuthContext)
    const [selected, setSelected] = useState<threadType | null>(null)
    const [assistants, setAssistants] = useState<AssistantType[]>([])
    const [assistant, setAssistant] = useState<AssistantType | null>(null)
    const [historyArray, setHistoryArray] = useState<threadType[]>([])
    const [projectArray, setProjectArray] = useState<project[]>([])
    const [questionArray, setQuestionArray] = useState<Question[]>([])
    const [messagesArray, setMessagesArray] = useState<messageType[]>([])
    const [limits, setLimits] = useState<limitsType>(limitsTemplate)
    const [openModelAlertLimit, setOpenModelAlertLimit] =
        useState<boolean>(false)
    const [archivedThread, setArchivedThread] = useState<threadType[]>([])
    const [archivedProjects, setArchivedProjects] = useState<project[]>([])
    const [usersOrg, setUsersOrg] = useState<Organization[]>([])
    const [orgUsersMetrics, setorgUsersMetrics] = useState<OrgMetrics>({
        org_name: '',
        org_guid: '',
        max_answers: 0,
        used_answers: 0,
        max_docs: 0,
        used_docs: 0,
        users_metrics: [],
    })
    const [tribunal, setTribunal] = useState<TribunaisType>(
        'Tribunais Superiores'
    )
    const [subscription, setSubscription] = useState<SubscriptionType | null>(
        null
    )
    const [alertVisibility, setAlertVisibility] =
        useState<string>('transparent')
    const [unarchieveModal, setUnarchieveModal] = useState<boolean>(false)
    const [isChat, setIsChat] = useState<boolean>(true)
    const [loadingMessageBot, setLoadingMessageBot] = useState<boolean>(false)
    const api = useMemo(() => new AssistantApi(), [])
    const documentApi = useMemo(() => new DocumentApi(), [])
    const flowApi = useMemo(() => new FlowApi(), [])
    const cookieService = new CookiesService()
    const [webSocketMessage, setWebSocketMessage] = useState<webSocketMessage>({
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    })
    const [redSocketMessage, setRedSocketMessage] = useState<webSocketMessage>({
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    })
    const [priceResearchSocketMessage, setPriceResearchSocketMessage] =
        useState<webSocketMessage>({
            status: '',
            project_id: '',
            timestamp: '',
            description: '',
            doc_url: '',
        })
    const [caronaSocketMessage, setCaronaSocketMessage] =
        useState<webSocketMessage>({
            status: '',
            project_id: '',
            timestamp: '',
            description: '',
            doc_url: '',
        })
    const [radarSocketMessage, setRadarSocketMessage] =
        useState<webSocketMessage>({
            status: '',
            project_id: '',
            timestamp: '',
            description: '',
            doc_url: '',
        })
    const [userDocumentsInfo, setUserDocumentsInfo] =
        useState<userDocumentsInfo | null>(null)
    const [uuid, setUuid] = useState<string>('')
    const [projectCost, setProjectCost] = useState<number>(0)
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [user, setUser] = useState<IUserInfo | null>(null)
    const [viewResume, setViewResume] = useState<boolean>(false)
    const [flowQuestions, setflowQuestions] = useState<QuestionFlow | null>(
        null
    )
    const [flows, setFlows] = useState<Array<{id: string; name: string}>>([])

    async function getSubscription() {
        try {
            const res = await api.getCurrentSubscription()
            setSubscription(res)
        } catch (error) {
            console.error('Erro ao obter subscription:', error)
        }
    }

    function isQuestion(obj: any): obj is Question {
        return (
            obj &&
            typeof obj.id === 'string' &&
            typeof obj.content === 'string' &&
            obj.type &&
            (obj.type.select ||
                obj.type.text ||
                obj.type.radio ||
                obj.type.table)
        )
    }

    async function getNextQuestion(project_id: string) {
        try {
            const res = await documentApi.getNextQuestion(project_id)

            if (isQuestion(res)) {
                setQuestionArray((prevItems) => [...prevItems, res])
                console.log('Nova pergunta adicionada:', res)
            } else {
                console.error(
                    'Resposta não corresponde à tipagem de Question:',
                    res
                )
            }
        } catch (error) {
            console.error('Erro ao buscar próxima pergunta:', error)
        }
    }

    async function getProjects() {
        try {
            const res = await documentApi.getProjects()
            setProjectArray(res)
        } catch (error) {
            console.error('Erro ao buscar projetos:', error)
        }
    }

    async function postProject(projectData: any) {
        try {
            const res = await documentApi.postProject(projectData)
            setQuestionArray([res])
            console.log('Projeto criado:', questionArray)
        } catch (error) {
            console.error('Erro ao criar projeto:', error)
        }
    }

    async function updateProject(projectData: any) {
        try {
            await documentApi.updateProject(projectData)
            getProjects()
            console.log('Projeto atualizado:', questionArray)
        } catch (error) {
            console.error('Erro ao atualizar projeto:', error)
        }
    }

    async function getFlow() {
        try {
            const res = await flowApi.getFlow()
            setFlows(res)
        } catch (error) {
            console.error('Erro ao buscar flow do projeto:', error)
        }
    }

    async function setFlowData(id: string) {
        const flowById = await flowApi.getFlowById(id)
        setflowQuestions(flowById)
    }

    async function updateFlow(flowData: any) {
        try {
            await flowApi.updateFlow(flowData)
        } catch (error) {
            console.error('Erro ao atualizar fluxo de documentos:', error)
        }
    }

    async function archiveProject(project_id: string) {
        try {
            await documentApi.archiveProject(project_id)
            getProjects()
            getArchivedProjects()
        } catch (error) {
            console.error('Erro ao arquivar projeto:', error)
        }
    }

    async function unarchiveProject(project_id: string) {
        try {
            await documentApi.unarchiveProject(project_id)
            getProjects()
            getArchivedProjects()
        } catch (error) {
            console.error('Erro ao desarquivar projeto:', error)
        }
    }

    async function getArchivedProjects() {
        try {
            const res = await documentApi.getArchivedProjects()
            setArchivedProjects(res)
        } catch (error) {
            console.error('Erro ao buscar projetos arquivados:', error)
        }
    }

    async function createNewChat(forceCreate: boolean = false): Promise<void> {
        console.log('Criando novo chat...')
        if (!forceCreate && historyArray.find((item) => item.guid === 'new')) {
            if (
                historyArray.findIndex(
                    (item) => item.guid === selected?.guid
                ) === 0
            ) {
                triggerAlertVisibility()
            } else
                setSelected(
                    historyArray[
                        historyArray.findIndex((item) => item.guid === 'new')
                    ]
                )
            return
        }
        const newChat: threadType = {
            created_at: new Date(),
            last_updated: new Date(),
            guid: 'new',
            name: 'Novo Chat',
            owner_id: '',
            uploaded_documents: 0,
        }
        setHistoryArray((prev) => [newChat, ...prev])
        console.log('Novo chat criado:', newChat)
        setSelected(newChat)
        return
    }

    async function archiveThread(guid: string) {
        await api.archiveThread(guid)
        getThreadsArchived()
        await getThreads()
    }

    async function unarchiveThread(guid: string) {
        await api.unarchiveThread(guid)
        await getThreadsArchived()
        await getThreads()
    }

    async function deleteThread(guid: string) {
        await api.deleteThread(guid)
        await getThreads()
    }

    function updateHistoryArrayAtIndex(index: number, updatedChat: threadType) {
        if (index !== -1) {
            setHistoryArray((prev) => {
                const newArray = [...prev]
                newArray[index] = updatedChat
                return newArray
            })
        }
    }

    async function updateCurrentThread(message: messageType) {
        console.log('Atualizando thread atual com a mensagem:', message)
        const currentLimits = await api.getLimits()
        setLimits(currentLimits)
        if (
            currentLimits?.usages?.messages?.current ===
            currentLimits?.usages?.messages?.max
        ) {
            setOpenModelAlertLimit(true)
            return
        }
        var threadName = 'Novo Chat'
        if (selected?.guid === 'new' && message.thread_id !== 'new') {
            const thread = await api.getThread(message.thread_id)

            threadName = thread.name
        }
        const updatedChat: threadType = {
            created_at: new Date(),
            last_updated: new Date(),
            guid: message.thread_id,
            name: threadName,
            owner_id: '',
            uploaded_documents: 0,
        }
        const newThreadIndex = historyArray.findIndex(
            (item) => item.guid === 'new'
        )
        updateHistoryArrayAtIndex(newThreadIndex, updatedChat)
        if (!message.author_id) {
            await getThreads()
            setSelected(updatedChat)
        }
    }
    function includeMessageAtArray(message: messageType) {
        if (message.author_id == null && message.assistant_id == null) {
            console.warn('Mensagem inválida - sem author_id e sem assistant_id')
            return
        }

        setSelected((currentSelected) => {
            if (currentSelected?.guid === 'new') {
                setMessagesArray(() => [message])
                updateCurrentThread(message)
            } else {
                if (currentSelected?.guid === message.thread_id) {
                    setMessagesArray((prev) => {
                        const messageExists = prev.some(
                            (msg) =>
                                new Date(msg.created_at).getTime() ===
                                new Date(message.created_at).getTime()
                        )

                        return messageExists ? prev : [...prev, message]
                    })
                }
                // updateCurrentThread(message)
            }
            return currentSelected
        })
    }

    function triggerAlertVisibility() {
        setAlertVisibility('red')
        setTimeout(() => {
            setAlertVisibility('transparent')
        }, 1500)
    }

    async function getMessages() {
        if (selected?.guid && api && selected?.guid !== 'new') {
            api.getMessages(selected?.guid)
                .then((response) => {
                    response?.length === 0
                        ? setMessagesArray([
                              {
                                  content: 'Olá, como posso ajudar?',
                                  thread_id: '',
                                  assistant_id: 'schief',
                                  author_id: '',
                                  created_at: new Date(),
                                  uploaded_documents: [],
                              },
                          ])
                        : setMessagesArray(response)
                })
                .catch((err) => {
                    setMessagesArray([
                        {
                            content: 'Olá, como posso ajudar?',
                            thread_id: '',
                            assistant_id: 'schief',
                            author_id: '',
                            created_at: new Date(),
                            uploaded_documents: [],
                        },
                    ])
                })
        } else {
            if (
                messagesArray.length === 0 ||
                messagesArray[0]?.thread_id !== selected?.guid
            ) {
                selected?.guid === 'new'
                    ? setMessagesArray([
                          {
                              content: 'Olá, como posso ajudar?',
                              thread_id: '',
                              assistant_id: 'schief',
                              author_id: '',
                              created_at: new Date(),
                              uploaded_documents: [],
                          },
                      ])
                    : setMessagesArray([])
            }
        }
    }

    async function getLimits() {
        return api
            .getLimits()
            .then((response: limitsType) => setLimits(response))
    }

    async function getThreads() {
        return api.getThreads().then((response: threadType[]) => {
            setHistoryArray(response)
            createNewChat(true)
        })
    }

    async function getAssistants() {
        return api.getAssistants().then((res) => {
            const assistantList = Array.isArray(res) ? res : []
            setAssistants(assistantList)
            const cookieService = new CookiesService()
            const c_assistant = cookieService.getAssistant()
            if (c_assistant) {
                setAssistant(c_assistant)
            } else {
                setAssistant(assistantList[0])
            }
        })
    }

    async function getThreadsArchived() {
        return api.getThreadsArchived().then((response: threadType[]) => {
            setArchivedThread(response)
        })
    }

    async function getOrgUsers() {
        return api.getOrganizationUsers().then((response: Organization[]) => {
            setUsersOrg(response)
        })
    }

    async function setOrganizationMaxUsages(
        metricas: {[guid: string]: {messages?: number; projects?: number}},
        defaultResources: boolean
    ) {
        const users_usages = Object.entries(metricas).map(([guid, usages]) => ({
            guid,
            max_messages: usages.messages ?? 0,
            max_documents: usages.projects ?? 0,
        }))

        const payload = {
            split_resources: defaultResources,
            users_limits: users_usages,
        }

        try {
            await api.setOrganizationMaxUsages(payload)
            await getLimits()
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async function getOrgUsersMetrics() {
        try {
            console.log('Buscando métricas dos usuários da organização...')
            const response = await api.getOrganizationUsersMetrics()
            setorgUsersMetrics(response)
        } catch (error) {
            console.error(
                'Erro ao buscar métricas dos usuários da organização:',
                error
            )
        }
    }

    async function getUserInfo() {
        try {
            const response = await documentApi.getUserInfo()
            setUserDocumentsInfo(response)
        } catch (error) {
            console.error('Erro ao buscar informações do usuário:', error)
        }
    }

    async function getUser() {
        try {
            const response = await api.getUser()
            setUser(response)
        } catch (error) {
            console.error('Erro ao buscar informações do usuário:', error)
        }
    }

    async function getResume(project_id: string) {
        try {
            const response = await documentApi.getResume(project_id)
            return response
        } catch (error) {
            console.error('Erro ao buscar resumo do projeto:', error)
            return null
        }
    }

    useEffect(() => {
        const token = cookieService.getAuthData().access_token
        let socket: WebSocket | null = null
        let pingInterval: NodeJS.Timeout | null = null
        let pongReceived = true
        let isConnecting = false

        let shouldReconnect = true

        const connectWebSocket = () => {
            if (!shouldReconnect) {
                return
            }

            if (isConnecting) {
                console.log('Já tentando conectar, ignorando nova tentativa...')
                return
            }

            isConnecting = true
            console.log('Iniciando conexão WebSocket...')

            if (socket) {
                socket.close()
            }
            if (pingInterval) {
                clearInterval(pingInterval)
                pingInterval = null
            }

            let wasOpened = false

            socket = new WebSocket(
                `${import.meta.env.VITE_WS_CORE_URL}/ws?token=${token}`
            )

            socket.onopen = () => {
                console.log('Conexão WebSocket aberta!')
                wasOpened = true
                isConnecting = false
                pongReceived = true

                pingInterval = setInterval(() => {
                    if (!pongReceived) {
                        console.log('Pong não recebido, reconectando...')
                        if (socket) {
                            socket.close()
                        }
                        return
                    }

                    if (socket && socket.readyState === WebSocket.OPEN) {
                        pongReceived = false
                        socket.send('ping')
                        // console.log('Ping enviado')
                    }
                }, 60000)
            }

            socket.onclose = (event) => {
                isConnecting = false
                if (pingInterval) {
                    clearInterval(pingInterval)
                    pingInterval = null
                }

                if (!wasOpened) {
                    console.log(
                        'Conexão WebSocket recusada, parando tentativas de reconexão.',
                        event.code,
                        event.reason
                    )
                    shouldReconnect = false
                    return
                }

                console.log(
                    'Conexão WebSocket fechada, tentando reconectar...',
                    event.code,
                    event.reason
                )

                setTimeout(() => {
                    connectWebSocket()
                }, 3000)
            }

            socket.onerror = (error) => {
                console.log('Erro na conexão WebSocket:', error)
                isConnecting = false
            }

            socket.onmessage = (event) => {
                console.log('Mensagem recebida:', event.data)

                if (event.data === 'pong') {
                    pongReceived = true
                    // console.log('Pong recebido')
                    return
                }

                try {
                    const message = JSON.parse(event.data)

                    if (message.module === 'document-analyzer') {
                        console.log(
                            'Mensagem de document analyzer recebida:',
                            message
                        )
                        setRedSocketMessage(message)
                    } else if (message.module === 'ata-inteligente') {
                        console.log(
                            'Mensagem do Ata Inteligente recebida:',
                            message
                        )
                        setCaronaSocketMessage(message)
                    } else if (message.module === 'chat') {
                        console.log('Mensagem de chat recebida:', message)
                        includeMessageAtArray(message)
                    } else if (message.module === 'document-generator') {
                        console.log('Mensagem de documento recebida:', message)
                        setWebSocketMessage(message)
                    } else if (message.module === 'price-research') {
                        console.log(
                            'Mensagem da Pesquisa de Preços recebida:',
                            message
                        )
                        setPriceResearchSocketMessage(message)
                    } else if (message.module === 'radar-fornecedor') {
                        setRadarSocketMessage(message)
                    } else {
                        console.log('Tipo de mensagem desconhecido:', message)
                    }
                } catch (error) {
                    const errorMessage = (error as any).message
                    console.error('Erro ao parsear JSON:', errorMessage)
                    console.error('Dados recebidos:', event.data)
                }
            }
        }

        connectWebSocket()

        return () => {
            if (pingInterval) {
                clearInterval(pingInterval)
            }
            if (socket) {
                socket.close()
            }
        }
    }, [])

    useEffect(() => {
        getMessages()
    }, [selected])

    useEffect(() => {
        getThreads()
        getLimits()
        getAssistants()
        getThreadsArchived()
        getSubscription()
        getUser()
    }, [api])

    useEffect(() => {
        getArchivedProjects()
        getUserInfo()
        
    }, [documentApi])

    useEffect(() => {
        if (
            webSocketMessage.status === 'FINISHED' ||
            webSocketMessage.description.includes('Error')
        ) {
            documentApi.getProjectById(webSocketMessage.project_id)
        }
    }, [webSocketMessage])

    return (
        <HistoryContext.Provider
            value={{
                selected,
                setSelected,
                historyArray,
                createNewChat,
                messagesArray,
                includeMessageAtArray,
                tribunal,
                setTribunal,
                assistant,
                setAssistant,
                limits,
                assistants,
                getLimits,
                archiveThread,
                unarchiveThread,
                deleteThread,
                updateCurrentThread,
                archivedThread,
                subscription,
                alertVisibility,
                unarchieveModal,
                setUnarchieveModal,
                isChat,
                setIsChat,
                projectArray,
                setMessagesArray,
                questionArray,
                setQuestionArray,
                getNextQuestion,
                getProjects,
                postProject: (projectData) => postProject(projectData),
                usersOrg,
                archiveProject,
                archivedProjects,
                unarchiveProject,
                webSocketMessage,
                openModelAlertLimit,
                setOpenModelAlertLimit,
                orgUsersMetrics,
                getOrgUsersMetrics,
                loadingMessageBot,
                setLoadingMessageBot,
                userDocumentsInfo,
                uuid,
                setUuid,
                updateProject: (projectData) => updateProject(projectData),
                setOrganizationMaxUsages,
                projectCost,
                setProjectCost,
                setSelectedFiles,
                selectedFiles,
                user,
                viewResume,
                setViewResume,
                setRedSocketMessage,
                redSocketMessage,
                priceResearchSocketMessage,
                setPriceResearchSocketMessage,
                caronaSocketMessage,
                setCaronaSocketMessage,
                setRadarSocketMessage,
                radarSocketMessage,
                flowQuestions,
                setFlowData,
                flows,
                setFlows,
                updateFlow,
                getFlow,
            }}
        >
            {children}
        </HistoryContext.Provider>
    )
}

export default HistoryProvider
